import React, { useState } from 'react'
import { Box, Button } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'

import ChatImage from '@/ui/components/ChatImage/ChatImage'
import useAssistantIllustration from '@/hooks/useAssistantIllustration'
import useWindowSize from '@/hooks/useWindowSize'
import CustomMarkdown from '@/ui/components/CustomMarkdown/CustomMarkdown'
import IssueReportDialog, { IssueReportDialogType } from './IssueReportDialog'
import type { IssueReportFieldKey } from '@/utils/issueReport'
import MsTeamIcon from '@/assets/ms-teams.svg'

import './ErrorRecoveryMessage.scss'

export interface ErrorRecoveryMessageProps {
    assistantImage?: string
    assistantImageAllowInversion?: boolean
    isDarkTheme?: boolean
    isRetryable: boolean
    errorPayload?: Record<string, unknown>
    onRetry?: () => void
    /**
     * How the report reaches the developers. `without-flow` (default): the user copies the information of the error
     * and posts it in the Teams channel "User Feedbacks". `with-flow`: the backend posts it (Power Automate flow),
     * for when `shareToDev` of mma-backend is approved.
     */
    issueReportType?: IssueReportDialogType
    /** The fields of the information of the error that the report dialog shows; see `reportFields` of `IssueReportDialog` */
    issueReportFields?: IssueReportFieldKey[]
}

const ErrorRecoveryMessageComponent = ({
    assistantImage,
    assistantImageAllowInversion,
    isDarkTheme,
    isRetryable,
    errorPayload,
    onRetry,
    issueReportType = 'without-flow',
    issueReportFields,
}: ErrorRecoveryMessageProps) => {
    const { t } = useTranslation()
    const { isMobile } = useWindowSize()
    const { backgroundColor: assistantImageBackgroundColor } = useAssistantIllustration()
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const errorMessage = isRetryable
        ? t('chat:errorRecovery:retryableMessage')
        : t('chat:errorRecovery:unrecoverableMessage')

    return (
        <div className="assistant-message">
            <div className="message-wrapper">
                <div className="sender-image-container">
                    <ChatImage
                        image={assistantImage}
                        imageAllowInversion={assistantImageAllowInversion}
                        isDarkTheme={isDarkTheme}
                        backgroundColor={assistantImageBackgroundColor}
                        size={isMobile ? 1.25 : 2}
                        rounded
                    />
                </div>
                <div>
                    <Box className="message-box message-box-chatbot message-box-error">
                        <div className="message-content">
                            <CustomMarkdown>{errorMessage}</CustomMarkdown>
                        </div>
                        <div className="error-actions">
                            {isRetryable && onRetry && (
                                <Button
                                    className="error-action-button retry-button"
                                    icon={{ iconName: 'refresh' }}
                                    label={t('chat:errorRecovery:retryButton')}
                                    mode='integrated'
                                    onClick={onRetry}
                                />
                            )}
                            {!isRetryable && (
                                <>
                                    <Button
                                        className="error-action-button share-to-dev-button"
                                        mode='integrated'
                                        onClick={() => setIsDialogOpen(true)}
                                    >
                                        <img src={MsTeamIcon} className="ms-teams-icon" alt="" aria-hidden="true" />
                                        <span>{t('chat:errorRecovery:shareToDevButton')}</span>
                                    </Button>
                                    <IssueReportDialog
                                        open={isDialogOpen}
                                        errorPayload={errorPayload}
                                        type={issueReportType}
                                        reportFields={issueReportFields}
                                        onClose={() => setIsDialogOpen(false)}
                                    />
                                </>
                            )}
                        </div>
                    </Box>
                </div>
            </div>
        </div>
    )
}

const ErrorRecoveryMessage = React.memo(ErrorRecoveryMessageComponent)

export default ErrorRecoveryMessage
